/**
 * Hook to check if an email exists in Clerk and detect authentication methods.
 *
 * Uses Clerk's signIn.create API to probe for email existence and available
 * authentication strategies (password vs passwordless).
 *
 * Aliases: email detection hook, user lookup hook
 */

import { useClerk } from "@clerk/tanstack-react-start";
import { useCallback, useState } from "react";

import {
  getClerkErrorMessage,
  isClerkAPIResponseError,
} from "~/lib/clerk-errors";

export interface UseEmailLookupResult {
  readonly lookupEmail: (email: string) => Promise<{
    exists: boolean;
    hasPassword: boolean;
  }>;
  readonly isLoading: boolean;
  readonly error: string;
  readonly clearError: () => void;
}

const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const useEmailLookup = (): UseEmailLookupResult => {
  const clerk = useClerk();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const clearError = useCallback(() => {
    setError("");
  }, []);

  const lookupEmail = useCallback(
    async (
      email: string,
    ): Promise<{ exists: boolean; hasPassword: boolean }> => {
      setIsLoading(true);
      setError("");

      try {
        if (!clerk?.client) {
          throw new Error("Clerk client not available");
        }

        const normalizedEmail = normalizeEmail(email);

        // Attempt to create a sign-in to check if email exists
        // This doesn't actually sign in, just checks availability
        const signIn = await clerk.client.signIn.create({
          identifier: normalizedEmail,
        });

        // Check if password is available as a first factor
        const hasPassword =
          signIn.supportedFirstFactors?.some(
            (factor) => factor.strategy === "password",
          ) ?? false;

        return { exists: true, hasPassword };
      } catch (err: unknown) {
        // Check for specific Clerk error indicating email doesn't exist
        if (isClerkAPIResponseError(err)) {
          const notFoundError = err.errors?.some(
            (clerkError) =>
              clerkError?.code === "form_identifier_not_found" ||
              clerkError?.code === "identifier_not_found" ||
              clerkError?.message?.toLowerCase().includes("couldn't find"),
          );

          if (notFoundError) {
            return { exists: false, hasPassword: false };
          }
        }

        // For other errors, set error state and rethrow
        const message = getClerkErrorMessage(err, "Failed to check email");
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [clerk],
  );

  return {
    lookupEmail,
    isLoading,
    error,
    clearError,
  };
};
